import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { todayStr } from '../lib/parse'
import { applyThemeToDOM, DEFAULT_THEME_ID } from '../themes'

export type ThemeMode = 'light' | 'dark' | 'system'

/** @deprecated Use ThemeMode */
export type Theme = ThemeMode

interface UIState {
  /** Currently focused/visible date in YYYY-MM-DD format */
  focusedDate: string
  sidebarOpen: boolean
  /** Desktop only: the notes list panel that sits beside the main sidebar */
  notesPanelOpen: boolean
  themeMode: ThemeMode
  themeId: string
  commandPaletteOpen: boolean
  /** How many consecutive days to show in the Week view (1, 3, 5, or 7) */
  weekDayCount: number
  /** When true, completed todos sort first, then the rest */
  smartStatusSort: boolean

  setFocusedDate: (date: string) => void
  toggleSidebar: () => void
  toggleNotesPanel: () => void
  openNotesPanel: () => void
  closeNotesPanel: () => void
  setThemeMode: (mode: ThemeMode) => void
  setThemeId: (id: string) => void
  toggleCommandPalette: () => void
  openCommandPalette: () => void
  closeCommandPalette: () => void
  setWeekDayCount: (n: number) => void
  setSmartStatusSort: (v: boolean) => void
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return mode
}

export function applyResolvedTheme(themeId: string, mode: ThemeMode): void {
  applyThemeToDOM(themeId, resolveMode(mode))
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      focusedDate: todayStr(),
      sidebarOpen: true,
      notesPanelOpen: false,
      themeMode: 'system',
      themeId: DEFAULT_THEME_ID,
      commandPaletteOpen: false,
      weekDayCount: 7,
      smartStatusSort: true,

      setFocusedDate: (date) => set({ focusedDate: date }),

      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

      toggleNotesPanel: () => set((s) => ({ notesPanelOpen: !s.notesPanelOpen })),

      openNotesPanel: () => set({ notesPanelOpen: true }),

      closeNotesPanel: () => set({ notesPanelOpen: false }),

      setThemeMode: (themeMode) => {
        set({ themeMode })
        applyResolvedTheme(get().themeId, themeMode)
      },

      setThemeId: (themeId) => {
        set({ themeId })
        applyResolvedTheme(themeId, get().themeMode)
      },

      toggleCommandPalette: () =>
        set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

      openCommandPalette: () => set({ commandPaletteOpen: true }),

      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      setWeekDayCount: (n) => set({ weekDayCount: n }),

      setSmartStatusSort: (v) => set({ smartStatusSort: v }),
    }),
    {
      name: 'todux-ui',
      version: 4,
      migrate: (persisted: unknown, fromVersion) => {
        const p = persisted as Record<string, unknown> | null
        if (fromVersion < 2 && p) {
          // Old key was `theme`; move to `themeMode`, default themeId.
          return {
            ...p,
            themeMode: (p.theme as ThemeMode | undefined) ?? 'system',
            themeId: (p.themeId as string | undefined) ?? DEFAULT_THEME_ID,
            smartStatusSort: (p.smartStatusSort as boolean | undefined) ?? true,
          }
        }
        if (fromVersion < 3 && p) {
          // Add smartStatusSort if missing from persisted state.
          return {
            ...p,
            smartStatusSort: (p.smartStatusSort as boolean | undefined) ?? true,
          }
        }
        if (fromVersion < 4 && p) {
          // v4 drops focusedDate from persistence — strip it from any old
          // blob so the default initializer (todayStr) wins on rehydrate.
          const { focusedDate: _drop, ...rest } = p
          return rest
        }
        return persisted
      },
      // focusedDate is intentionally NOT persisted: each app open should land
      // on today rather than wherever the user was scrolled to last session.
      // If they want to come back to a specific week, they can navigate.
      partialize: (s) => ({
        sidebarOpen: s.sidebarOpen,
        notesPanelOpen: s.notesPanelOpen,
        themeMode: s.themeMode,
        themeId: s.themeId,
        weekDayCount: s.weekDayCount,
        smartStatusSort: s.smartStatusSort,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyResolvedTheme(state.themeId, state.themeMode)
          // Defensive: a previous build (version <4) may have persisted
          // focusedDate. Reset it so opening the app always lands on today
          // even if old storage is read before the version bump migrates.
          state.focusedDate = todayStr()
        }
      },
    }
  )
)

// Listen for system theme changes and re-apply if mode === 'system'
if (typeof window !== 'undefined') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      const { themeId, themeMode } = useUIStore.getState()
      if (themeMode === 'system') applyResolvedTheme(themeId, 'system')
    })
}
